import { useState, useRef, useEffect, useCallback } from 'react';
import {
    User, Plus, Shield, MessageSquare, Edit, Wrench, Link2,
    ChevronRight, ChevronDown, Clock, CheckCircle, Fingerprint, Award,
    Calendar, Tag, Globe, Mic, Brain, Settings,
    Download, Share2, AlertTriangle, Skull, Check,
    History, Eye, EyeOff
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';
import { useTranslation } from 'react-i18next';
import { UserReplica, ReplicaFormData } from '../types';
import { getAuth } from 'firebase/auth';
import { replicaService } from '../lib/services';
import ReplicaCapabilitiesCard from './ReplicaCapabilitiesCard';

interface MyReplicaViewProps {
    onNavigate: (view: string) => void;
}

export default function MyReplicaView({ onNavigate }: MyReplicaViewProps) {
    const { user } = useAuth();
    const { getUserReplicas, deleteReplica } = useReplica();
    const { t } = useTranslation('dashboard');

    // Fetch replicas from backend API (source of truth) with fallback to context
    const contextReplicas = user?.uid ? getUserReplicas(user.uid) : [];
    const [backendReplicas, setBackendReplicas] = useState<UserReplica[]>([]);
    const [loadingBackend, setLoadingBackend] = useState(true);

    useEffect(() => {
        if (!user?.uid) { setLoadingBackend(false); return; }
        replicaService.getUserReplicas(user.uid).then(res => {
            if (res.data?.replicas && res.data.replicas.length > 0) {
                const mapped: UserReplica[] = res.data.replicas.map((r: any) => ({
                    id: r.id,
                    name: r.name || 'Unnamed Replica',
                    category: r.category || 'General',
                    description: r.description || '',
                    creatorId: r.ownerId || user.uid,
                    status: r.operationalStatus === 'KILLED' ? 'killed' as const
                        : r.operationalStatus === 'SUSPENDED' ? 'suspended' as const
                            : r.status === 'ACTIVE' || r.status === 'deployed' ? 'deployed' as const
                                : 'draft' as const,
                    createdAt: r.createdAt || new Date().toISOString(),
                    updatedAt: r.updatedAt || new Date().toISOString(),
                    rating: r.rating ?? 0,
                    reviews: r.reviewCount ?? 0,
                    parentId: null,
                    price: { amount: r.priceAmount ?? 0, period: (r.pricePeriod || 'hour') as 'hour' | 'month' },
                    hasHumanGuardian: !!r.guardianAssignment && r.guardianAssignment !== 'no-guardian',
                    image: r.avatarImageUrl || '',
                    skills: r.expertise ? [r.expertise] : [],
                    supportedTasks: [],
                    killedAt: r.operationalStatus === 'KILLED' ? (r.statusChangedAt || r.updatedAt || new Date().toISOString()) : undefined,
                    killReason: r.operationalStatus === 'KILLED' ? (r.statusReason || 'Owner-initiated termination') : undefined,
                    formData: {
                        name: r.name || '',
                        category: r.category || '',
                        description: r.description || '',
                        avatarStyle: r.avatarStyle || 'realistic',
                        gender: r.avatarGender || 'neutral',
                        ageRange: r.avatarAge || 'adult',
                        ethnicity: r.avatarEthnicity || 'diverse',
                        voiceTone: r.voiceTone || 'professional',
                        voiceSpeed: r.voiceSpeed ?? 1.0,
                        voiceSamples: [],
                        expertise: r.expertise || '',
                        language: r.language || 'English',
                        responseSpeed: r.responseSpeed || 'fast',
                        selectedPersonalityTraits: r.personalityTraits
                            ? (typeof r.personalityTraits === 'string'
                                ? r.personalityTraits.split(', ').filter(Boolean)
                                : Array.isArray(r.personalityTraits) ? r.personalityTraits : [])
                            : [],
                        personalityDescription: r.personality || '',
                        trainingMaterials: [],
                        sampleConversations: '',
                        dosAndDonts: '',
                        boundaries: {
                            noExternalComms: false, noFinancialData: false,
                            noPersonnelDecisions: false, noTransactions: false,
                            noLegalInquiries: false, customBoundaries: [],
                        },
                        guardianConfig: {
                            guardianType: (r.guardianAssignment || 'self') as any,
                            financialThreshold: 1000,
                            externalCommsPolicy: 'draft-only' as const,
                            customEscalationRules: [],
                        },
                        consent: {
                            understandsLimitations: true,
                            acceptsResponsibility: true,
                            confirmedGuardianSetup: true,
                        },
                        deploymentOption: (r.deploymentOption || 'website-widget') as any,
                        guardianAssignment: (r.guardianAssignment || 'self') as any,
                    } as ReplicaFormData,
                }));
                setBackendReplicas(mapped);
            }
        }).catch(err => {
            console.warn('Failed to fetch replicas from backend, using local context:', err);
        }).finally(() => setLoadingBackend(false));
    }, [user?.uid]);

    // Merge: backend replicas take priority, then add any context-only replicas not in backend
    const backendIds = new Set(backendReplicas.map(r => r.id));
    const userReplicas = [
        ...backendReplicas,
        ...contextReplicas.filter(r => !backendIds.has(r.id)),
    ];

    // Split into active replicas and killed (history)
    const activeReplicas = userReplicas.filter(r => r.status !== 'killed');
    const killedReplicas = userReplicas.filter(r => r.status === 'killed');

    const [expandedReplica, setExpandedReplica] = useState<string | null>(
        activeReplicas.length === 1 ? activeReplicas[0]?.id : null
    );
    const [showKillConfirm, setShowKillConfirm] = useState<string | null>(null);
    const [killReason, setKillReason] = useState('');
    const [killLoading, setKillLoading] = useState(false);
    const [showCertificate, setShowCertificate] = useState<string | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const [toastMessage, setToastMessage] = useState<string | null>(null);

    // Show toast with auto-dismiss
    const showToast = useCallback((msg: string) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 3000);
    }, []);

    // ═══════════════════════════════════════════════════════════════════════════
    // KILL REPLICA HANDLER
    // ═══════════════════════════════════════════════════════════════════════════
    const handleKillReplica = async (replicaId: string) => {
        setKillLoading(true);
        const deathTime = new Date().toISOString();
        const reason = killReason || 'Owner-initiated kill from My Replica page';
        try {
            // Try API call first
            await fetch(`/api/v1/sovereignty/${replicaId}/kill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason }),
            });
        } catch (e) {
            console.warn('API kill failed, updating locally:', e);
        }
        // Update status to killed locally (moves to History section)
        deleteReplica(replicaId);
        setBackendReplicas(prev => prev.map(r =>
            r.id === replicaId
                ? { ...r, status: 'killed' as const, killedAt: deathTime, killReason: reason }
                : r
        ));
        setShowKillConfirm(null);
        setKillReason('');
        setKillLoading(false);
        setShowHistory(true); // Auto-open history to show the moved replica
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // GENERATE CERTIFICATE DATA
    // ═══════════════════════════════════════════════════════════════════════════
    const generateCertificateData = (replica: UserReplica) => {
        const fingerprint = btoa(`${replica.id}-${replica.createdAt}-${replica.creatorId}`).slice(0, 32);
        return {
            agentId: replica.id,
            agentName: replica.name,
            creationDate: replica.createdAt,
            trainer: {
                name: user?.displayName || user?.email || 'Owner',
                id: replica.creatorId,
                signature: btoa(replica.creatorId).slice(0, 16),
            },
            fingerprint,
            qrData: `humanreplica://${replica.id}/${fingerprint}`,
        };
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // LOADING STATE — wait for backend before showing "no replicas"
    // ═══════════════════════════════════════════════════════════════════════════
    if (loadingBackend && userReplicas.length === 0) {
        return (
            <div className="max-w-4xl mx-auto pb-12">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-hig-text-primary tracking-tight mb-2">
                        {t('my_replica_view.title', 'My Replica')}
                    </h1>
                    <p className="text-hig-text-secondary">
                        {t('my_replica_view.subtitle', 'View and manage your Human Replicas')}
                    </p>
                </div>
                <div className="card p-14 text-center">
                    <div className="animate-pulse flex flex-col items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-hig-bg-tertiary" />
                        <div className="h-4 w-48 bg-hig-bg-tertiary rounded" />
                        <div className="h-3 w-32 bg-hig-bg-tertiary rounded" />
                    </div>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // EMPTY STATE
    // ═══════════════════════════════════════════════════════════════════════════
    if (userReplicas.length === 0) {
        return (
            <div className="max-w-4xl mx-auto pb-12">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-hig-text-primary tracking-tight mb-2">
                        {t('my_replica_view.title', 'My Replica')}
                    </h1>
                    <p className="text-hig-text-secondary">
                        {t('my_replica_view.subtitle', 'View and manage your Human Replicas')}
                    </p>
                </div>

                <div className="card p-14 lg:p-20 text-center border-2 border-dashed border-hig-accent/20 bg-gradient-to-b from-hig-accent/[0.03] to-transparent">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-hig-accent/15 to-indigo-500/10 flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <User size={40} className="text-hig-accent" />
                    </div>
                    <h2 className="text-2xl font-bold text-hig-text-primary mb-4 tracking-tight">
                        {t('personal_dashboard.no_replicas_yet')}
                    </h2>
                    <p className="text-hig-text-secondary mb-10 max-w-md mx-auto leading-relaxed text-base">
                        {t('personal_dashboard.create_first_replica')}
                    </p>
                    <button
                        onClick={() => onNavigate('create-replica')}
                        className="btn-primary px-10 py-4 rounded-2xl font-semibold text-base shadow-lg shadow-hig-accent/20"
                    >
                        <Plus size={20} className="mr-2" />
                        {t('hero.create_cta')}
                    </button>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // BIRTH CERTIFICATE MODAL
    // ═══════════════════════════════════════════════════════════════════════════
    const BirthCertificate = ({ replica }: { replica: UserReplica }) => {
        const cert = generateCertificateData(replica);
        const certRef = useRef<HTMLDivElement>(null);
        const [isDownloading, setIsDownloading] = useState(false);
        const [serverCert, setServerCert] = useState<any>(null);
        // Fetch or issue certificate from backend
        useEffect(() => {
            const fetchOrIssueCert = async () => {
                try {
                    const auth = getAuth();
                    const token = await auth.currentUser?.getIdToken();
                    if (!token) { return; }

                    // Try to fetch existing certificate
                    let res = await fetch(`/api/v1/certificates/replica/${replica.id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });

                    if (res.ok) {
                        const data = await res.json();
                        setServerCert(data.certificate);
                    } else if (res.status === 404) {
                        // Issue a new certificate
                        res = await fetch(`/api/v1/certificates/${replica.id}/issue`, {
                            method: 'POST',
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                        });
                        if (res.ok) {
                            const data = await res.json();
                            setServerCert(data.certificate);
                        }
                    }
                } catch (e) {
                    console.warn('Could not fetch server certificate, using local data:', e);
                }
            };
            fetchOrIssueCert();
        }, [replica.id]);

        // Use server cert data if available, otherwise fall back to local
        const displayFingerprint = serverCert?.fingerprint || cert.fingerprint;
        const displaySignature = serverCert
            ? serverCert.signature.slice(0, 24) + '...'
            : cert.trainer.signature;
        const displayCertId = serverCert?.id || cert.agentId;
        const isServerSigned = !!serverCert;

        // ── Download as PNG ──
        const handleDownload = async () => {
            if (!certRef.current || isDownloading) return;
            setIsDownloading(true);
            try {
                const canvas = await html2canvas(certRef.current, {
                    backgroundColor: '#0d1b2a',
                    scale: 2,
                    useCORS: true,
                    logging: false,
                });
                const link = document.createElement('a');
                link.download = `${replica.name}_Birth_Certificate.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
                showToast('Certificate downloaded!');
            } catch (err) {
                console.error('Download failed:', err);
                showToast('Download failed. Please try again.');
            }
            setIsDownloading(false);
        };

        // ── Share ──
        const handleShare = async () => {
            const verifyUrl = serverCert
                ? `${window.location.origin}/verify/${serverCert.id}`
                : `${window.location.origin}/replica/${replica.id}`;

            const shareData = {
                title: `${replica.name} — Birth Certificate`,
                text: `Verified Birth Certificate for AI Replica "${replica.name}", created by ${cert.trainer.name}.`,
                url: verifyUrl,
            };

            try {
                if (navigator.share && navigator.canShare?.(shareData)) {
                    await navigator.share(shareData);
                } else {
                    await navigator.clipboard.writeText(verifyUrl);
                    showToast('Verification link copied to clipboard!');
                }
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    // Fallback: copy to clipboard
                    try {
                        await navigator.clipboard.writeText(verifyUrl);
                        showToast('Verification link copied to clipboard!');
                    } catch {
                        showToast('Could not share. Please copy the link manually.');
                    }
                }
            }
        };

        const isKilled = replica.status === 'killed';
        const borderColor = isKilled ? 'border-red-500/30' : 'border-amber-500/30';
        const shadowColor = isKilled ? 'shadow-red-500/10' : 'shadow-amber-500/10';
        const accentColor = isKilled ? 'red' : 'amber';

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                onClick={() => setShowCertificate(null)}
            >
                <div
                    className={`w-full max-w-lg bg-gradient-to-br from-[#0d1b2a] via-[#1b2838] to-[#0d1b2a] rounded-3xl border ${borderColor} shadow-2xl ${shadowColor} overflow-hidden`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Capturable area for download */}
                    <div ref={certRef}>
                        {/* Header */}
                        <div className={`text-center p-8 pb-4 border-b border-${accentColor}-500/20`}>
                            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${isKilled ? 'from-red-500 to-red-700' : 'from-amber-400 to-amber-600'} flex items-center justify-center mx-auto mb-4 shadow-lg ${isKilled ? 'shadow-red-500/30' : 'shadow-amber-500/30'}`}>
                                {isKilled ? <Skull size={32} className="text-white" /> : <Award size={32} className="text-white" />}
                            </div>
                            <h2 className={`text-xl font-bold ${isKilled ? 'text-red-300' : 'text-amber-300'} tracking-wide mb-1`}>
                                {isKilled ? 'Death Certificate' : t('create_replica.ai_replica_birth_certificate')}
                            </h2>
                            <p className={`text-xs ${isKilled ? 'text-red-400/60' : 'text-amber-400/60'} tracking-widest uppercase`}>
                                {isKilled ? 'Record of Permanent Termination' : 'Cryptographically Signed Authenticity Document'}
                            </p>
                        </div>

                        {/* Certificate Body */}
                        <div className="p-8 space-y-5">
                            {/* Death Details — only for killed replicas */}
                            {isKilled && replica.killedAt && (
                                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                                    <p className="text-xs text-red-400/50 uppercase tracking-wider mb-3">
                                        Termination Record
                                    </p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-hig-text-tertiary">Date of Death</p>
                                            <p className="text-sm font-semibold text-red-300">
                                                {new Date(replica.killedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-hig-text-tertiary">Time of Death</p>
                                            <p className="text-sm font-semibold text-red-300">
                                                {new Date(replica.killedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                            </p>
                                        </div>
                                        <div className="col-span-2">
                                            <p className="text-xs text-hig-text-tertiary">Cause of Death</p>
                                            <p className="text-sm font-semibold text-red-300">
                                                {replica.killReason || 'Owner-initiated termination'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Replica Details (Birth Info) */}
                            <div>
                                <p className={`text-xs ${isKilled ? 'text-hig-text-tertiary' : 'text-amber-400/50'} uppercase tracking-wider mb-2`}>
                                    {isKilled ? 'Birth Record' : t('create_replica.replica_details')}
                                </p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-hig-text-tertiary">{t('create_replica.name')}</p>
                                        <p className="text-sm font-semibold text-hig-text-primary">{cert.agentName}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-hig-text-tertiary">{t('create_replica.creation_date')}</p>
                                        <p className="text-sm font-semibold text-hig-text-primary">
                                            {new Date(cert.creationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-hig-text-tertiary">{t('create_replica.certificate_id')}</p>
                                        <p className={`text-sm font-mono ${isKilled ? 'text-red-400' : 'text-amber-400'}`}>{displayCertId.slice(0, 12)}...</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-hig-text-tertiary">{t('create_replica.creator')}</p>
                                        <p className="text-sm font-semibold text-hig-text-primary">{cert.trainer.name}</p>
                                    </div>
                                    {isKilled && (
                                        <div className="col-span-2">
                                            <p className="text-xs text-hig-text-tertiary">Lifespan</p>
                                            <p className="text-sm font-semibold text-hig-text-secondary">
                                                {Math.max(1, Math.ceil((new Date(replica.killedAt!).getTime() - new Date(cert.creationDate).getTime()) / (1000 * 60 * 60 * 24)))} days
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Cryptographic Verification */}
                            <div className={`${isKilled ? 'bg-red-500/5 border-red-500/20' : 'bg-emerald-500/5 border-emerald-500/20'} border rounded-xl p-4`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <CheckCircle size={16} className={isKilled ? 'text-red-400' : 'text-emerald-400'} />
                                    <p className={`text-sm font-semibold ${isKilled ? 'text-red-400' : 'text-emerald-400'}`}>
                                        {isKilled ? 'Termination Verified' : isServerSigned ? 'Cryptographically Signed' : 'Local Verification'}
                                    </p>
                                    {(isServerSigned || isKilled) && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${isKilled ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'} font-bold uppercase tracking-wider`}>
                                            {isKilled ? 'Final' : 'Verified'}
                                        </span>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <div>
                                        <p className="text-xs text-hig-text-tertiary">SHA-256 Fingerprint</p>
                                        <p className={`text-xs font-mono ${isKilled ? 'text-red-300/80' : 'text-emerald-300/80'} break-all`}>{displayFingerprint.slice(0, 40)}...</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-hig-text-tertiary">HMAC Signature</p>
                                        <p className={`text-xs font-mono ${isKilled ? 'text-red-300/80' : 'text-emerald-300/80'}`}>{displaySignature}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Ownership Proof / Final Record */}
                            <div className={`${isKilled ? 'bg-red-500/5 border-red-500/15' : 'bg-amber-500/5 border-amber-500/15'} border rounded-xl p-4`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Fingerprint size={16} className={isKilled ? 'text-red-400' : 'text-amber-400'} />
                                    <p className={`text-sm font-semibold ${isKilled ? 'text-red-300' : 'text-amber-300'}`}>
                                        {isKilled ? 'Permanent Record' : 'Proof of Ownership'}
                                    </p>
                                </div>
                                <p className="text-xs text-hig-text-secondary leading-relaxed">
                                    {isKilled
                                        ? <>This Replica was permanently terminated by its owner <strong className="text-hig-text-primary">{cert.trainer.name}</strong>. All operations have been halted and this record is maintained for audit purposes. This action was irreversible.</>
                                        : <>This certificate proves that this Replica was created by and belongs to <strong className="text-hig-text-primary">{cert.trainer.name}</strong>. The owner retains full sovereignty over this Replica, including the right to suspend, modify, or permanently terminate it at any time.</>
                                    }
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Actions — outside capturable area */}
                    <div className="p-6 pt-0 flex items-center gap-3">
                        <button
                            onClick={handleDownload}
                            disabled={isDownloading}
                            className="flex-1 btn-secondary py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <Download size={16} />
                            {isDownloading ? 'Saving...' : isKilled ? 'Download Death Certificate' : t('create_replica.download_certificate')}
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex-1 btn-secondary py-3 rounded-xl text-sm flex items-center justify-center gap-2"
                        >
                            <Share2 size={16} />
                            {t('create_replica.share_certificate')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // KILL CONFIRMATION DIALOG
    // ═══════════════════════════════════════════════════════════════════════════
    const KillConfirmDialog = ({ replicaId, replicaName }: { replicaId: string; replicaName: string }) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowKillConfirm(null)}
        >
            <div
                className="w-full max-w-md bg-hig-bg-secondary rounded-2xl border border-red-500/30 shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-red-500/20 bg-red-950/20">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-red-600/20 flex items-center justify-center">
                            <AlertTriangle size={24} className="text-red-400" />
                        </div>
                        <div>
                            <h3 className="font-bold text-red-400 text-lg">Kill Replica</h3>
                            <p className="text-sm text-red-400/70">This action is irreversible</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <p className="text-sm text-hig-text-secondary mb-4 leading-relaxed">
                        You are about to permanently terminate <strong className="text-hig-text-primary">{replicaName}</strong>.
                        This will halt ALL operations, cancel pending tasks, and create a signed audit record.
                        This action <strong className="text-red-400">cannot be undone</strong>.
                    </p>

                    <input
                        type="text"
                        placeholder="Reason for termination (optional)"
                        value={killReason}
                        onChange={(e) => setKillReason(e.target.value)}
                        className="w-full bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2.5 text-sm text-hig-text-primary placeholder-red-500/40 mb-4 focus:outline-none focus:ring-1 focus:ring-red-500/50"
                    />

                    <div className="flex items-center gap-3 justify-end">
                        <button
                            onClick={() => setShowKillConfirm(null)}
                            className="px-5 py-2.5 rounded-lg text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => handleKillReplica(replicaId)}
                            disabled={killLoading}
                            className="px-6 py-2.5 rounded-lg text-sm font-bold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                            {killLoading ? 'Terminating...' : 'Confirm Kill'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // REPLICA DETAIL CARD
    // ═══════════════════════════════════════════════════════════════════════════
    const ReplicaDetailCard = ({ replica }: { replica: UserReplica }) => {
        const isExpanded = expandedReplica === replica.id;
        const formData = replica.formData;

        return (
            <div className="card overflow-hidden transition-all duration-300">
                {/* Header row */}
                <div
                    className="p-6 flex items-center justify-between cursor-pointer hover:bg-hig-bg-elevated/30 transition-colors"
                    onClick={() => setExpandedReplica(isExpanded ? null : replica.id)}
                >
                    <div className="flex items-center gap-5">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-blue-500/15">
                            {replica.name.charAt(0)}
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-1.5">
                                <h3 className="font-bold text-hig-text-primary text-lg">{replica.name}</h3>
                                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${replica.status === 'killed'
                                    ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                    : replica.status === 'suspended'
                                        ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                                        : replica.status === 'deployed'
                                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                            : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                    }`}>
                                    {replica.status === 'killed' ? 'Killed'
                                        : replica.status === 'suspended' ? 'Suspended'
                                            : replica.status === 'deployed' ? 'Representing'
                                                : t('personal_dashboard.draft')}
                                </span>
                                <span className="text-xs px-2.5 py-1 rounded-full bg-hig-accent/10 text-hig-accent border border-hig-accent/20 font-medium">
                                    Owned by you
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-hig-text-secondary">
                                <div className="flex items-center gap-1.5">
                                    <Tag size={13} />
                                    <span>{replica.category}</span>
                                </div>
                                <span className="text-hig-text-tertiary">•</span>
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={13} />
                                    <span>Created {new Date(replica.createdAt).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Quick Actions */}
                        <button
                            onClick={(e) => { e.stopPropagation(); onNavigate(`chat-${replica.id}`); }}
                            className="p-2.5 text-hig-text-tertiary hover:text-hig-accent hover:bg-hig-accent/10 rounded-xl transition-colors"
                            title="Chat"
                        >
                            <MessageSquare size={17} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onNavigate(`tools-${replica.id}`); }}
                            className="p-2.5 text-hig-text-tertiary hover:text-orange-400 hover:bg-orange-400/10 rounded-xl transition-colors"
                            title="Tools"
                        >
                            <Wrench size={17} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onNavigate(`mcp-tools-${replica.id}`); }}
                            className="p-2.5 text-hig-text-tertiary hover:text-cyan-400 hover:bg-cyan-400/10 rounded-xl transition-colors"
                            title="MCP Tools"
                        >
                            <Brain size={17} />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onNavigate(`edit-replica-${replica.id}`); }}
                            className="p-2.5 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-xl transition-colors"
                            title="Edit"
                        >
                            <Edit size={17} />
                        </button>
                        <ChevronRight
                            size={18}
                            className={`text-hig-text-tertiary transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
                        />
                    </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                    <div className="border-t border-hig-divider">
                        {/* ─── EXPANDED CONTENT ───────────────────────────── */}
                        <div className="animate-fade-in">
                            {/* Description */}
                            {replica.description && (
                                <div className="p-6 border-b border-hig-divider">
                                    <p className="text-sm text-hig-text-secondary leading-relaxed">{replica.description}</p>
                                </div>
                            )}

                            {/* Detail Grid */}
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {/* Expertise */}
                                {formData?.expertise && (
                                    <div className="bg-hig-bg-tertiary/50 rounded-xl p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Brain size={15} className="text-hig-accent" />
                                            <p className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider">Expertise</p>
                                        </div>
                                        <p className="text-sm text-hig-text-primary leading-relaxed">{formData.expertise}</p>
                                    </div>
                                )}

                                {/* Personality */}
                                {formData?.selectedPersonalityTraits?.length > 0 && (
                                    <div className="bg-hig-bg-tertiary/50 rounded-xl p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <User size={15} className="text-purple-400" />
                                            <p className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider">Personality</p>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {formData.selectedPersonalityTraits.map((trait: string) => (
                                                <span key={trait} className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20">
                                                    {trait}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Voice */}
                                {formData?.voiceTone && (
                                    <div className="bg-hig-bg-tertiary/50 rounded-xl p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Mic size={15} className="text-emerald-400" />
                                            <p className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider">Voice</p>
                                        </div>
                                        <p className="text-sm text-hig-text-primary capitalize">{formData.voiceTone}</p>
                                        <p className="text-xs text-hig-text-tertiary mt-1">
                                            Speed: {formData.voiceSpeed || 1.0}x
                                            {formData.voiceSamples?.filter((s: any) => s.isRecorded)?.length > 0 && (
                                                <> • {formData.voiceSamples.filter((s: any) => s.isRecorded).length} samples recorded</>
                                            )}
                                        </p>
                                    </div>
                                )}

                                {/* Language */}
                                {formData?.language && (
                                    <div className="bg-hig-bg-tertiary/50 rounded-xl p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Globe size={15} className="text-hig-accent" />
                                            <p className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider">Language</p>
                                        </div>
                                        <p className="text-sm text-hig-text-primary capitalize">{formData.language}</p>
                                    </div>
                                )}

                                {/* Guardian */}
                                <div className="bg-hig-bg-tertiary/50 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Shield size={15} className="text-emerald-400" />
                                        <p className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider">Guardian</p>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <CheckCircle size={14} className="text-emerald-400" />
                                        <p className="text-sm text-emerald-400 font-medium">Active</p>
                                    </div>
                                    {formData?.guardianConfig && (
                                        <p className="text-xs text-hig-text-tertiary mt-1 capitalize">
                                            Type: {formData.guardianConfig.guardianType}
                                        </p>
                                    )}
                                </div>

                                {/* Deployment */}
                                {formData?.deploymentOption && (
                                    <div className="bg-hig-bg-tertiary/50 rounded-xl p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Settings size={15} className="text-hig-text-secondary" />
                                            <p className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider">Deployment</p>
                                        </div>
                                        <p className="text-sm text-hig-text-primary capitalize">
                                            {formData.deploymentOption.replace(/-/g, ' ')}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="p-6 pt-0 flex flex-wrap items-center gap-3">
                                <button
                                    onClick={() => setShowCertificate(replica.id)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium ${replica.status === 'killed'
                                        ? 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20'
                                        : 'bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20'
                                        } transition-all`}
                                >
                                    {replica.status === 'killed' ? <Skull size={16} /> : <Award size={16} />}
                                    {replica.status === 'killed' ? 'View Death Certificate' : 'View Birth Certificate'}
                                </button>
                                
                                {replica.status !== 'killed' && (
                                    <button
                                        onClick={() => onNavigate(`identity-${replica.id}`)}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"
                                    >
                                        <Link2 size={16} />
                                        Identity Links
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ─── GLOBAL ACTIONS (Always visible contextually) ───── */}
                        {replica.status !== 'killed' && (
                            <div className="px-6 pb-6 pt-2 flex justify-end">
                                <button
                                    onClick={() => setShowKillConfirm(replica.id)}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-hig-text-tertiary hover:text-red-400 hover:bg-red-500/10 transition-all opacity-50 hover:opacity-100"
                                >
                                    <Skull size={14} />
                                    Kill Replica (Permanent)
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // MAIN RENDER
    // ═══════════════════════════════════════════════════════════════════════════
    return (
        <div className="max-w-5xl mx-auto pb-12">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-hig-text-primary tracking-tight mb-2">
                        {t('my_replica_view.title', 'My Replica')}
                    </h1>
                    <p className="text-hig-text-secondary">
                        {t('my_replica_view.subtitle_with_count', {
                            count: activeReplicas.length,
                            defaultValue: `You own ${activeReplicas.length} active Replica${activeReplicas.length !== 1 ? 's' : ''}`
                        })}
                    </p>
                </div>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                <div className="card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-hig-accent/10 flex items-center justify-center">
                        <User size={20} className="text-hig-accent" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-hig-text-primary">{activeReplicas.length}</p>
                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Total Active</p>
                    </div>
                </div>
                <div className="card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <CheckCircle size={20} className="text-emerald-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-hig-text-primary">
                            {activeReplicas.filter(r => r.status === 'deployed').length}
                        </p>
                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Representing</p>
                    </div>
                </div>
                <div className="card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
                        <Clock size={20} className="text-amber-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-hig-text-primary">
                            {activeReplicas.filter(r => r.status === 'draft').length}
                        </p>
                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Drafts</p>
                    </div>
                </div>
                <div className="card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-red-500/10 flex items-center justify-center">
                        <Skull size={20} className="text-red-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-hig-text-primary">{killedReplicas.length}</p>
                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Killed</p>
                    </div>
                </div>
            </div>

            {/* Active Replica Cards */}
            {activeReplicas.length > 0 ? (
                <div className="space-y-4">
                    {activeReplicas.map((replica) => (
                        <ReplicaDetailCard key={replica.id} replica={replica} />
                    ))}
                </div>
            ) : (
                <div className="card p-14 lg:p-20 text-center border-2 border-dashed border-hig-accent/20 bg-gradient-to-b from-hig-accent/[0.03] to-transparent">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-hig-accent/15 to-indigo-500/10 flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <User size={40} className="text-hig-accent" />
                    </div>
                    <h2 className="text-2xl font-bold text-hig-text-primary mb-4 tracking-tight">
                        {t('personal_dashboard.no_replicas_yet')}
                    </h2>
                    <p className="text-hig-text-secondary mb-10 max-w-md mx-auto leading-relaxed text-base">
                        {t('personal_dashboard.create_first_replica')}
                    </p>
                    <button
                        onClick={() => onNavigate('create-replica')}
                        className="btn-primary px-10 py-4 rounded-2xl font-semibold text-base shadow-lg shadow-hig-accent/20"
                    >
                        <Plus size={20} className="mr-2" />
                        {t('hero.create_cta')}
                    </button>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* HISTORY SECTION — Killed Replicas                                  */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            {killedReplicas.length > 0 && (
                <div className="mt-8">
                    {/* History Toggle Header */}
                    <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="w-full flex items-center justify-between p-4 rounded-2xl bg-hig-bg-secondary/50 border border-hig-divider hover:bg-hig-bg-secondary/80 transition-colors group"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-red-500/10 flex items-center justify-center">
                                <History size={18} className="text-red-400" />
                            </div>
                            <div className="text-left">
                                <p className="text-sm font-semibold text-hig-text-primary">
                                    History
                                </p>
                                <p className="text-xs text-hig-text-tertiary">
                                    {killedReplicas.length} terminated Replica{killedReplicas.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {showHistory
                                ? <EyeOff size={16} className="text-hig-text-tertiary group-hover:text-hig-text-secondary" />
                                : <Eye size={16} className="text-hig-text-tertiary group-hover:text-hig-text-secondary" />
                            }
                            <ChevronDown
                                size={18}
                                className={`text-hig-text-tertiary transition-transform duration-300 ${showHistory ? 'rotate-180' : ''}`}
                            />
                        </div>
                    </button>

                    {/* History Cards */}
                    {showHistory && (
                        <div className="mt-4 space-y-4">
                            {killedReplicas.map((replica) => (
                                <div key={replica.id} className="opacity-75 hover:opacity-100 transition-opacity">
                                    <ReplicaDetailCard replica={replica} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Sovereignty Notice */}
            <div className="mt-8 rounded-2xl bg-gradient-to-r from-hig-bg-secondary via-hig-bg-tertiary/50 to-hig-bg-secondary border border-hig-divider p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-hig-accent/10 flex items-center justify-center flex-shrink-0">
                    <Shield size={24} className="text-hig-accent" />
                </div>
                <div>
                    <p className="font-semibold text-hig-text-primary text-sm mb-0.5">You are the sovereign owner</p>
                    <p className="text-xs text-hig-text-secondary leading-relaxed">
                        Your Replicas operate under your authority. You can suspend, modify, or permanently kill any Replica at any time.
                        No enterprise can override your decisions — they hire your Replica, they don't own it.
                    </p>
                </div>
            </div>

            {/* Modals */}
            {showCertificate && (
                <BirthCertificate replica={userReplicas.find(r => r.id === showCertificate)!} />
            )}
            {showKillConfirm && (
                <KillConfirmDialog
                    replicaId={showKillConfirm}
                    replicaName={userReplicas.find(r => r.id === showKillConfirm)?.name || 'Replica'}
                />
            )}

            {/* Toast Notification */}
            {toastMessage && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] animate-fade-in">
                    <div className="bg-hig-bg-elevated border border-hig-border rounded-xl px-5 py-3 shadow-2xl flex items-center gap-2">
                        <Check size={16} className="text-emerald-400" />
                        <span className="text-sm text-hig-text-primary font-medium">{toastMessage}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
